import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Search,
  Check,
  Clock,
  Sparkles,
  Loader2,
  ArrowRight,
  AlertTriangle,
  Plus,
  Mail,
  MessageCircle,
  Briefcase,
  CreditCard,
  ShoppingBag,
  Globe,
  HeartPulse,
  Home,
  Image as ImageIcon,
  FileText,
  Plane,
  Utensils,
  Clapperboard,
  Repeat,
  Plug,
  Users,
  Upload,
  X,
  ExternalLink,
  Settings2,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type ConnectorCatalogCategory,
  type ConnectorCatalogEntry,
  type ConnectorCatalogStatus,
} from "../lib/api";

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  Mail,
  MessageCircle,
  Briefcase,
  CreditCard,
  ShoppingBag,
  Globe,
  HeartPulse,
  Home,
  Image: ImageIcon,
  FileText,
  Plane,
  Utensils,
  Clapperboard,
  Repeat,
  Users,
};

/** Where each export-import service tells the user to start their
 *  "Download Your Information" request. */
const EXPORT_HELP: Record<string, { url: string; label: string; minutes: string }> = {
  facebook: { url: "https://www.facebook.com/dyi/", label: "facebook.com/dyi", minutes: "Facebook can take a few hours" },
  instagram: { url: "https://www.instagram.com/download/request/", label: "instagram.com/download", minutes: "Instagram can take a few hours" },
  twitter_x: { url: "https://x.com/settings/download_your_data", label: "x.com/settings", minutes: "X is usually minutes to a day" },
  threads: { url: "https://www.instagram.com/download/request/", label: "instagram.com/download", minutes: "Threads ships in the Instagram export" },
};

type StatusFilter = "all" | ConnectorCatalogStatus;

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "built", label: "Built" },
  { id: "in_progress", label: "In progress" },
  { id: "planned", label: "Planned" },
];

/** Stable color per connector derived from its slug so the monogram
 *  tiles look intentional rather than random across reloads. */
const TILE_TONES = [
  "from-sky-500/30 to-sky-600/10 text-sky-100",
  "from-violet-500/30 to-violet-600/10 text-violet-100",
  "from-emerald-500/30 to-emerald-600/10 text-emerald-100",
  "from-amber-500/30 to-amber-600/10 text-amber-100",
  "from-rose-500/30 to-rose-600/10 text-rose-100",
  "from-cyan-500/30 to-cyan-600/10 text-cyan-100",
  "from-indigo-500/30 to-indigo-600/10 text-indigo-100",
];
function toneFor(slug: string): string {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return TILE_TONES[h % TILE_TONES.length];
}

function StatusChip({ status }: { status: ConnectorCatalogStatus }) {
  if (status === "built") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
        <Check className="w-3 h-3" /> Built
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30">
        <Clock className="w-3 h-3" /> Coming soon
      </span>
    );
  }
  if (status === "deprecated") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-400/30">
        Disabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-400/30">
      Planned
    </span>
  );
}

function relativeSync(iso: string | null): string {
  if (!iso) return "never synced";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "synced";
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 1) return "synced just now";
  if (mins < 60) return `synced ${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `synced ${hrs}h ago`;
  return `synced ${Math.round(hrs / 24)}d ago`;
}

interface ExportLatest {
  id: string;
  status: string;
  items_total: number;
  items_new: number;
  items_known: number;
  uploaded_at: string;
  finished_at: string | null;
  error: string | null;
}

/** Scope radio for the Configure step. Mirrors the canonical scope
 *  vocabulary the connectors table CHECK enforces after migration
 *  0097 (private|work|business|personal|family|social|financial|
 *  side_project) — the value chosen here is the connector-signal-
 *  strongest scope every imported packet inherits. */
const SCOPE_CHOICES: { label: string; value: string }[] = [
  { label: "Personal", value: "personal" },
  { label: "Work", value: "work" },
  { label: "Family", value: "family" },
  { label: "Private", value: "private" },
  { label: "Financial", value: "financial" },
  { label: "Business", value: "business" },
  { label: "Side project", value: "side_project" },
];

/** Action surface for auth_kind = 'data_export_import'. Meta / X
 *  killed personal-data APIs, so the only path is the user's own
 *  downloaded export zip.
 *
 *  Lifecycle (driven by c.instance, the connector-instance row, NOT
 *  catalog status):
 *    no instance            -> "Set up"            (Configure modal)
 *    configured, no import   -> "Import export"
 *    configured, has import  -> "Import another export"
 *
 *  Flicker fix: the modal's visibility (`modal`) is a single discrete
 *  state decoupled from the polled progress (`latest`); the polled
 *  value is read through useDeferredValue so a burst of poll writes
 *  cannot thrash the open modal; the file <input> is uncontrolled and
 *  reached via a ref so it is never recreated across renders; and the
 *  status poll is a single stable effect (stable useCallback, 1000ms)
 *  instead of an unstable closure passed straight to useEffect. */
function DataExportImport({
  c,
  onChanged,
}: {
  c: ConnectorCatalogEntry;
  onChanged: () => void;
}) {
  const [latest, setLatest] = useState<ExportLatest | null>(null);
  const [modal, setModal] = useState<"none" | "configure" | "import">("none");
  const [busy, setBusy] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [label, setLabel] = useState(
    c.instance?.account_label || `My ${c.display_name}`,
  );
  const [acctId, setAcctId] = useState(c.instance?.account_identifier ?? "");
  // No-hardcoded-scope (item 3): nothing pre-selected. The user must
  // consciously pick before the Configure submit enables. Reconfigure
  // pre-fills from the existing instance's saved scope.
  const [scopeChoice, setScopeChoice] = useState(
    SCOPE_CHOICES.find((s) => s.value === c.instance?.scope)?.label ?? "",
  );

  const fileRef = useRef<HTMLInputElement | null>(null);
  const help = EXPORT_HELP[c.slug] ?? EXPORT_HELP.facebook;

  const configured = !!c.instance?.configured;
  const hasDone = !!c.instance?.has_done_export;

  const refresh = useCallback(() => {
    api
      .connectorExportStatus(c.slug)
      .then((d) => setLatest(d.latest))
      .catch(() => undefined);
  }, [c.slug]);

  // Initial status read once configured; nothing to import before that.
  useEffect(() => {
    if (configured) refresh();
  }, [configured, refresh]);

  // Single stable poll: only while an import is actually in flight.
  const inFlight = latest?.status === "queued" || latest?.status === "parsing";
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(refresh, 1000);
    return () => clearInterval(t);
  }, [inFlight, refresh]);

  // Read polled progress through a deferred value so a burst of poll
  // writes can't re-thrash the (decoupled) modal subtree.
  const shownLatest = useDeferredValue(latest);

  const acctName = c.instance?.account_label || `My ${c.display_name}`;
  const pill = (() => {
    if (!configured) return "Set up to connect";
    if (shownLatest?.status === "queued" || shownLatest?.status === "parsing")
      return "Importing…";
    if (shownLatest?.status === "error") return "Import failed";
    if (hasDone && c.instance) {
      const iso = c.instance.last_import_at;
      const d = iso ? new Date(iso) : null;
      const when = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
      const n = (c.instance.last_import_items ?? 0).toLocaleString();
      return `${acctName} · Last import: ${when} · ${n} items`;
    }
    return `Configured: ${acctName} · ready to import`;
  })();

  const primaryLabel = !configured
    ? "Set up"
    : hasDone
      ? "Import another export"
      : "Import export";

  const closeModal = useCallback(() => {
    setModal("none");
    setMsg(null);
  }, []);

  // These modals already portal to document.body (so they center on
  // the viewport, not the scrolled list). Lock body scroll while one
  // is open and wire Escape-to-close for parity with ConfigureModal.
  useEffect(() => {
    if (modal === "none") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [modal, closeModal]);

  const doConfigure = useCallback(async () => {
    if (busy) return;
    const trimmedLabel = label.trim();
    const trimmedId = acctId.trim();
    if (!trimmedLabel || !trimmedId) {
      setMsg("Account label and account identity are both required.");
      return;
    }
    const scope = SCOPE_CHOICES.find((s) => s.label === scopeChoice)?.value;
    if (!scope) {
      // No-hardcoded-scope: the user must consciously pick. We never
      // default; the backend also rejects a missing scope.
      setMsg("Pick the scope this connector's data lands in.");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.configureConnector(c.slug, {
        account_label: trimmedLabel,
        account_identifier: trimmedId,
        scope,
      });
      closeModal();
      onChanged();
    } catch (e) {
      setMsg((e as Error).message || "Could not save");
    } finally {
      setBusy(false);
    }
  }, [busy, scopeChoice, label, acctId, c.slug, closeModal, onChanged]);

  const doUpload = useCallback(async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.uploadConnectorExport(c.slug, f);
      setMsg("Uploaded. Memso is parsing your export now.");
      if (fileRef.current) fileRef.current.value = "";
      setHasFile(false);
      refresh();
      onChanged();
      setTimeout(closeModal, 1200);
    } catch (e) {
      setMsg((e as Error).message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }, [busy, c.slug, refresh, onChanged, closeModal]);

  const onPrimary = useCallback(() => {
    setMsg(null);
    setModal(configured ? "import" : "configure");
  }, [configured]);

  return (
    <div className="mt-auto pt-1 space-y-1.5">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {pill}
        {shownLatest &&
        shownLatest.status === "done" &&
        shownLatest.items_new !== shownLatest.items_total ? (
          <span className="text-emerald-600 dark:text-emerald-400">
            {" "}
            ({shownLatest.items_new.toLocaleString()} new,{" "}
            {shownLatest.items_known.toLocaleString()} already known)
          </span>
        ) : null}
      </p>
      <button
        type="button"
        onClick={onPrimary}
        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white"
      >
        {configured ? (
          <Upload className="w-3.5 h-3.5" />
        ) : (
          <Settings2 className="w-3.5 h-3.5" />
        )}
        {primaryLabel}
      </button>

      {/* Modals are portaled to <body>. The connector card carries a
          `hover:-translate-y-0.5` transform; a transformed ancestor
          becomes the containing block for position:fixed children, so
          a fixed modal nested in the card would reposition on every
          card-hover toggle — the ~60fps "flicker like crazy" loop.
          Portaling lifts the modal out of the card's transform
          context so the loop can't form. */}
      {modal === "configure" &&
        createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-[min(28rem,90vw)] max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Set up {c.display_name}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Tell Memso which {c.display_name} account this is and choose the
              scope its data lands in. Everything you import here is tagged with
              that scope.
            </p>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                Account label
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={`e.g. ${c.display_name} - Personal`}
                className="block w-full rounded-lg bg-white/70 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10 px-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                Account identity
              </label>
              <input
                type="text"
                value={acctId}
                onChange={(e) => setAcctId(e.target.value)}
                placeholder="Profile URL or username (e.g. waqas.ahmed)"
                className="block w-full rounded-lg bg-white/70 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10 px-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
              <p className="text-[10px] text-slate-400 dark:text-slate-500">
                The profile URL or username this export belongs to. Used to keep
                accounts straight when you connect more than one.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                Scope
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {SCOPE_CHOICES.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => setScopeChoice(s.label)}
                    className={
                      "text-xs font-medium rounded-lg px-2.5 py-1.5 border text-left transition-colors " +
                      (scopeChoice === s.label
                        ? "bg-sky-600 border-sky-500 text-white"
                        : "bg-transparent border-slate-300 dark:border-white/15 text-slate-600 dark:text-slate-300 hover:border-sky-400")
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {msg && (
              <p className="text-[11px] text-rose-500">{msg}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="text-xs font-semibold rounded-lg px-3 py-1.5 border border-slate-300 dark:border-white/15 text-slate-600 dark:text-slate-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doConfigure}
                disabled={
                  busy || !label.trim() || !acctId.trim() || !scopeChoice
                }
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === "import" &&
        createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-[min(28rem,90vw)] max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Import your {c.display_name} data
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="text-xs text-slate-600 dark:text-slate-300 space-y-2 list-decimal list-inside">
              <li>
                Go to{" "}
                <a
                  href={help.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky-600 dark:text-sky-400 inline-flex items-center gap-0.5 font-medium"
                >
                  {help.label} <ExternalLink className="w-3 h-3" />
                </a>{" "}
                and request a data export (choose JSON / "Download Your Information").
              </li>
              <li>Wait for the email confirmation that it is ready ({help.minutes}).</li>
              <li>Download the zip from the link in that email.</li>
              <li>Upload the zip here:</li>
            </ol>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              onChange={() => setHasFile(!!fileRef.current?.files?.length)}
              className="block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-sky-600 file:text-white hover:file:bg-sky-700"
            />
            {msg && <p className="text-[11px] text-slate-500 dark:text-slate-400">{msg}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="text-xs font-semibold rounded-lg px-3 py-1.5 border border-slate-300 dark:border-white/15 text-slate-600 dark:text-slate-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doUpload}
                disabled={!hasFile || busy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function ConnectorCard({
  c,
  mode,
  selected,
  onToggleSelect,
  onRequested,
  onChanged,
}: {
  c: ConnectorCatalogEntry;
  mode: "settings" | "onboarding";
  selected: boolean;
  onToggleSelect: (slug: string) => void;
  onRequested: (slug: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(c.requested);

  const monogram = c.display_name.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";

  const doRequest = async () => {
    if (busy || requested) return;
    setBusy(true);
    try {
      await api.requestConnector(c.slug);
      setRequested(true);
      onRequested(c.slug);
    } catch {
      /* surfaced via the unchanged state */
    } finally {
      setBusy(false);
    }
  };

  // slug -> { provider (google-oauth `provider` param == connectors.kind),
  // default scope }. Each Google service is its own connector with its
  // own minimal OAuth scope set + its own Connect button; the OAuth
  // flow uses prompt=select_account so a second click adds another
  // account instance rather than re-grant the same one.
  const GOOGLE_SLUGS: Record<string, { provider: string; scope: string }> = {
    gmail: { provider: "gmail", scope: "personal" },
    google_calendar: { provider: "calendar", scope: "personal" },
    google_photos: { provider: "google_photos", scope: "family" },
    google_drive: { provider: "google_drive", scope: "personal" },
    google_contacts: { provider: "google_contacts", scope: "personal" },
  };

  const goConnect = () => {
    const g = GOOGLE_SLUGS[c.slug];
    if (g) {
      window.location.href = `/api/connectors/google/start?provider=${g.provider}&scope=${g.scope}&returnTo=/settings/connectors`;
      return;
    }
    if (c.config_route) window.location.href = c.config_route;
  };

  const isGoogle = !!GOOGLE_SLUGS[c.slug];
  const connectLabel =
    c.slug === "google_photos"
      ? c.connected
        ? "Connect family account"
        : "Connect family account"
      : c.connected && isGoogle
        ? "Connect another account"
        : "Connect";

  const selectable = mode === "onboarding";

  return (
    <div
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      onClick={selectable ? () => onToggleSelect(c.slug) : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleSelect(c.slug);
              }
            }
          : undefined
      }
      className={
        "group relative flex flex-col gap-3 rounded-2xl border p-4 transition-all duration-150 " +
        (selectable
          ? selected
            ? "cursor-pointer border-sky-400/70 bg-sky-500/10 ring-2 ring-sky-400/40 shadow-lg"
            : "cursor-pointer border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20"
          : "border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] hover:shadow-lg hover:-translate-y-0.5 dark:hover:bg-white/[0.06]")
      }
    >
      {c.is_recommended && (
        <span className="absolute -top-2 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-sky-500 text-white shadow">
          <Sparkles className="w-2.5 h-2.5" /> Recommended
        </span>
      )}
      <div className="flex items-start gap-3">
        <span
          className={
            "w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-xs font-extrabold flex-shrink-0 border border-white/10 " +
            toneFor(c.slug)
          }
        >
          {monogram}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
              {c.display_name}
            </h4>
            {c.connected ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                <Check className="w-3 h-3" /> Connected
              </span>
            ) : (
              <StatusChip status={c.status} />
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
            {c.description}
          </p>
          {c.connected && (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400/80 mt-1">
              {relativeSync(c.last_sync_at)}
            </p>
          )}
        </div>
      </div>

      {!selectable && c.auth_kind === "data_export_import" && (
        <DataExportImport c={c} onChanged={onChanged} />
      )}

      {!selectable && c.auth_kind !== "data_export_import" && (
        <div className="mt-auto pt-1">
          {c.connected && isGoogle ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={goConnect}
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white"
              >
                <Plus className="w-3.5 h-3.5" /> {connectLabel}
              </button>
              <a
                href="/settings/connectors"
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/20"
              >
                Manage
              </a>
            </div>
          ) : c.connected ? (
            <button
              type="button"
              onClick={goConnect}
              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/20"
            >
              Manage
            </button>
          ) : c.status === "deprecated" ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Disabled: Google retired its photo read API. Use the Memso
              Android Companion to capture photos going forward.
            </p>
          ) : c.status === "built" ? (
            <button
              type="button"
              onClick={goConnect}
              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white"
            >
              {connectLabel} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : c.status === "in_progress" ? (
            <button
              type="button"
              onClick={doRequest}
              disabled={busy || requested}
              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-400/30 hover:bg-amber-500/20 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {requested ? "We'll notify you" : "Notify me"}
            </button>
          ) : (
            <button
              type="button"
              onClick={doRequest}
              disabled={busy || requested}
              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 bg-white/5 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-white/15 hover:bg-white/10 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {requested ? "Requested" : "Request"}
            </button>
          )}
        </div>
      )}

      {selectable && (
        <span
          className={
            "absolute top-3 right-3 w-5 h-5 rounded-full border flex items-center justify-center transition-colors " +
            (selected
              ? "bg-sky-500 border-sky-400 text-white"
              : "border-white/30 text-transparent")
          }
        >
          <Check className="w-3 h-3" />
        </span>
      )}
    </div>
  );
}

export function ConnectorCatalog({
  mode = "settings",
  selected,
  onSelectedChange,
}: {
  mode?: "settings" | "onboarding";
  selected?: string[];
  onSelectedChange?: (slugs: string[]) => void;
}) {
  const [categories, setCategories] = useState<ConnectorCatalogCategory[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [lifecycleTab, setLifecycleTab] = useState<"available" | "configured">(
    "available",
  );

  const selectedSet = useMemo(() => new Set(selected ?? []), [selected]);

  const reload = useCallback(() => {
    api
      .connectorCatalog()
      .then((d) => setCategories(d.categories))
      .catch((e) => setErr((e as Error).message));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleSelect = (slug: string) => {
    if (!onSelectedChange) return;
    const next = new Set(selectedSet);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    onSelectedChange([...next]);
  };

  const toggleCategoryFilter = (slug: string) => {
    setActiveCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  if (err) {
    return (
      <div className="text-sm text-rose-500">Could not load the connector catalog: {err}</div>
    );
  }
  if (!categories) {
    return (
      <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the connector catalog…
      </div>
    );
  }

  // A connector is "Configured" once it has a live connection (OAuth /
  // QR connectors) or a configured instance row (data-export
  // connectors). Drives the Available vs Configured lifecycle tabs.
  const isConfigured = (c: ConnectorCatalogEntry) =>
    c.connected || !!c.instance?.configured;
  const lifecycleTabs = mode === "settings";

  const q = query.trim().toLowerCase();
  const filtered = categories
    .filter((cat) => activeCategories.length === 0 || activeCategories.includes(cat.slug))
    .map((cat) => ({
      ...cat,
      connectors: cat.connectors.filter((c) => {
        if (lifecycleTabs) {
          const configured = isConfigured(c);
          if (lifecycleTab === "configured" && !configured) return false;
          if (lifecycleTab === "available" && configured) return false;
        }
        if (statusFilter !== "all") {
          if (statusFilter === "planned") {
            if (c.status !== "planned" && c.status !== "request_only") return false;
          } else if (c.status !== statusFilter) {
            return false;
          }
        }
        if (!q) return true;
        return (
          c.display_name.toLowerCase().includes(q) ||
          (c.description ?? "").toLowerCase().includes(q) ||
          c.slug.includes(q)
        );
      }),
    }))
    .filter((cat) => cat.connectors.length > 0);

  const total = categories.reduce((n, cat) => n + cat.connectors.length, 0);
  const connectedTotal = categories.reduce(
    (n, cat) => n + cat.connectors.filter((c) => c.connected).length,
    0,
  );

  const actionNeeded =
    mode === "settings"
      ? categories.flatMap((cat) =>
          cat.connectors.filter(
            (c) =>
              c.status === "built" &&
              !c.connected &&
              c.auth_kind === "qr_pair" &&
              !!c.config_route,
          ),
        )
      : [];

  return (
    <div className="space-y-5">
      {actionNeeded.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-bold">Action required</span>
          </div>
          <ul className="mt-2 space-y-1.5">
            {actionNeeded.map((c) => (
              <li
                key={c.slug}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="text-slate-700 dark:text-slate-200">
                  <span className="font-semibold">{c.display_name}</span> is
                  built but not paired yet.
                </span>
                <a
                  href={c.config_route ?? "#"}
                  className="inline-flex items-center gap-1 flex-shrink-0 text-xs font-semibold rounded-lg px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Pair now <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Search + status filters */}
      <div className="space-y-3">
        {lifecycleTabs && (
          <div className="inline-flex rounded-xl border border-slate-300 dark:border-white/15 p-0.5 bg-white/60 dark:bg-white/[0.03]">
            {(
              [
                {
                  id: "available" as const,
                  label: "Available",
                  n: categories.reduce(
                    (acc, cat) =>
                      acc + cat.connectors.filter((x) => !isConfigured(x)).length,
                    0,
                  ),
                },
                {
                  id: "configured" as const,
                  label: "Configured",
                  n: categories.reduce(
                    (acc, cat) =>
                      acc + cat.connectors.filter((x) => isConfigured(x)).length,
                    0,
                  ),
                },
              ]
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setLifecycleTab(t.id)}
                className={
                  "text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors " +
                  (lifecycleTab === t.id
                    ? "bg-sky-600 text-white"
                    : "text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-white")
                }
              >
                {t.label} ({t.n})
              </button>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${total} connectors…`}
            className="w-full rounded-xl bg-white/70 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10 pl-9 pr-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={
                "text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors " +
                (statusFilter === f.id
                  ? "bg-sky-600 border-sky-500 text-white"
                  : "bg-transparent border-slate-300 dark:border-white/15 text-slate-500 dark:text-slate-300 hover:border-sky-400")
              }
            >
              {f.label}
            </button>
          ))}
          {connectedTotal > 0 && (
            <span className="text-[11px] text-emerald-600 dark:text-emerald-400 ml-1">
              {connectedTotal} connected
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {categories.map((cat) => {
            const Icon = (cat.icon && CATEGORY_ICONS[cat.icon]) || Plug;
            const on = activeCategories.includes(cat.slug);
            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => toggleCategoryFilter(cat.slug)}
                className={
                  "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border transition-colors " +
                  (on
                    ? "bg-violet-600 border-violet-500 text-white"
                    : "bg-transparent border-slate-300 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-violet-400")
                }
              >
                <Icon className="w-3 h-3" /> {cat.display_name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category sections */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-white/15 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
          {lifecycleTabs && lifecycleTab === "configured"
            ? "Nothing configured yet. Set up a connector from the Available tab."
            : "No connectors match that filter."}
        </div>
      ) : (
        filtered.map((cat) => {
          const Icon = (cat.icon && CATEGORY_ICONS[cat.icon]) || Plug;
          return (
            <section key={cat.slug} className="space-y-3">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-white/10 to-white/[0.02] border border-white/10 flex items-center justify-center text-sky-500 dark:text-sky-300">
                  <Icon className="w-4 h-4" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    {cat.display_name}
                  </h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {cat.description} · {cat.connectors.length} shown
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cat.connectors.map((c) => (
                  <ConnectorCard
                    key={c.slug}
                    c={c}
                    mode={mode}
                    selected={selectedSet.has(c.slug)}
                    onToggleSelect={toggleSelect}
                    onRequested={() => undefined}
                    onChanged={reload}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
